import { $logger } from '@/boot/logger'

/**
 * Customer Return Error Handler Composable
 *
 * Provides centralized error handling for the customer return flow with
 * consistent logging and user-friendly notifications.
 */

/**
 * Error messages for customer return operations
 */
const ERROR_MESSAGES = {
  ORDER_FETCH: 'Unable to retrieve your order. Please verify your order number and try again.',
  ORDER_FETCH_GENERIC: 'There was an error loading your order. Please try again.',
  RETAILER_LOOKUP: 'Unable to find a retailer for this order. Please check your order details.',
  RETAILER_LOOKUP_GENERIC: 'There was an error looking up your order. Please try again.',
  SESSION_START: 'Failed to establish a return session. Please try again.',
  SESSION_ITEM_START: 'There was an error initializing the return process. Please try again.',
  SUBMISSION: 'Unable to submit your return. Please try again.',
  SUBMISSION_GENERIC: 'There was an error processing your return. Please try again.',
  MEDIA_UPLOAD: 'There was an error uploading the photo. Please try again.',
  MEDIA_UPLOAD_GENERIC: 'Failed to upload media. Please try again.',
  POLICY_CHECK: 'Unable to verify return policy. Please try again.',
  NETWORK: 'Network error. Please check your connection and try again.',
  UNKNOWN: 'An unexpected error occurred. Please try again.',
}

/**
 * Composable for customer return error handling
 * @returns {Object} Error handler functions
 */
export function useCustomerReturnErrorHandler() {
  const $q = useQuasar()

  /**
   * Show error notification to user
   * @param {string} message - Error message to display
   * @param {string} [type='error-long'] - Notification type
   */
  function showErrorNotification(message, type = 'error-long') {
    $q.notify({
      message,
      type,
    })
  }

  /**
   * Determine user-friendly message from error object
   * @param {Error|Object} error - Error object
   * @param {string} defaultMessage - Default message to use
   * @returns {string} User-friendly error message
   */
  function getUserMessage(error, defaultMessage) {
    // Check if error has a user-facing message
    if (error?.userMessage) {
      return error.userMessage
    }

    // Check for API error messages
    if (error?.data?.message) {
      return error.data.message
    }

    if (error?.message) {
      return error.message
    }

    return defaultMessage
  }

  /**
   * Handle order fetch errors
   * @param {Error|Object} error - Error object
   * @param {Object} context - Additional context for logging
   * @returns {Object} Error result object
   */
  function handleOrderFetchError(error, context = {}) {
    const message = getUserMessage(error, ERROR_MESSAGES.ORDER_FETCH_GENERIC)

    $logger.error('Customer return: Order fetch failed', {
      error,
      context,
    })

    showErrorNotification(message)

    return {
      success: false,
      error,
      message,
    }
  }

  /**
   * Handle retailer lookup errors
   * @param {Error|Object} error - Error object
   * @param {Object} context - Additional context for logging
   * @returns {Object} Error result object
   */
  function handleRetailerLookupError(error, context = {}) {
    const message = getUserMessage(error, ERROR_MESSAGES.RETAILER_LOOKUP_GENERIC)

    $logger.error('Customer return: Retailer lookup failed', {
      error,
      context,
    })

    showErrorNotification(message)

    return {
      success: false,
      error,
      message,
    }
  }

  /**
   * Handle session start errors
   * @param {Error|Object} error - Error object
   * @param {Object} context - Additional context for logging
   * @returns {Object} Error result object
   */
  function handleSessionStartError(error, context = {}) {
    const message = getUserMessage(error, ERROR_MESSAGES.SESSION_START)

    $logger.error('Customer return: Session start failed', {
      error,
      context,
    })

    showErrorNotification(message)

    return {
      success: false,
      error,
      message,
    }
  }

  /**
   * Handle session item start errors
   * @param {Error|Object} error - Error object
   * @param {Object} context - Additional context for logging
   * @returns {Object} Error result object
   */
  function handleSessionItemStartError(error, context = {}) {
    const message = getUserMessage(error, ERROR_MESSAGES.SESSION_ITEM_START)

    $logger.error('Customer return: Session item start failed', {
      error,
      context,
    })

    showErrorNotification(message)

    return {
      success: false,
      error,
      message,
    }
  }

  /**
   * Handle return submission errors
   * @param {Error|Object} error - Error object
   * @param {Object} context - Additional context for logging
   * @returns {Object} Error result object
   */
  function handleSubmissionError(error, context = {}) {
    const message = getUserMessage(error, ERROR_MESSAGES.SUBMISSION_GENERIC)

    $logger.error('Customer return: Submission failed', {
      error,
      context,
    })

    // Note: Toast notification is handled by AppReturnActions to support
    // dismiss-on-retry behavior. Only logging is done here.

    return {
      success: false,
      error,
      message,
    }
  }

  /**
   * Handle media upload errors
   * @param {Error|Object} error - Error object
   * @param {string} mediaType - Type of media being uploaded
   * @param {Object} context - Additional context for logging
   * @returns {Object} Error result object
   */
  function handleMediaUploadError(error, mediaType, context = {}) {
    const message = getUserMessage(error, ERROR_MESSAGES.MEDIA_UPLOAD_GENERIC)

    $logger.error('Customer return: Media upload failed', {
      error,
      mediaType,
      context,
    })

    showErrorNotification(message)

    return {
      success: false,
      error,
      message,
      mediaType,
    }
  }

  /**
   * Handle return policy check errors
   * @param {Error|Object} error - Error object
   * @param {Object} context - Additional context for logging
   * @returns {Object} Error result object
   */
  function handlePolicyCheckError(error, context = {}) {
    const message = getUserMessage(error, ERROR_MESSAGES.POLICY_CHECK)

    $logger.error('Customer return: Policy check failed', {
      error,
      context,
    })

    showErrorNotification(message)

    return {
      success: false,
      error,
      message,
    }
  }

  /**
   * Handle generic/unknown errors
   * @param {Error|Object} error - Error object
   * @param {string} operation - Name of operation that failed
   * @param {Object} context - Additional context for logging
   * @returns {Object} Error result object
   */
  function handleGenericError(error, operation, context = {}) {
    const message = getUserMessage(error, ERROR_MESSAGES.UNKNOWN)

    $logger.error(`Customer return: ${operation} failed`, {
      error,
      context,
    })

    showErrorNotification(message)

    return {
      success: false,
      error,
      message,
      operation,
    }
  }

  return {
    handleGenericError,
    handleMediaUploadError,
    handleOrderFetchError,
    handlePolicyCheckError,
    handleRetailerLookupError,
    handleSessionItemStartError,
    handleSessionStartError,
    handleSubmissionError,
  }
}
